import { useState, useEffect } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";
import { activateKeepAwakeAsync, deactivateKeepAwake } from "expo-keep-awake";
import { File, Directory, Paths } from "expo-file-system";
import { FirebaseServices } from "./firebase/config";
import { createMedia } from "./firebase/media-functions";
import { readFileAsBase64, MAX_SAFE_BASE64_FILE_SIZE_BYTES } from "@/utils/file-reader";
import { logAndFlush } from "./batch-logger";
import {
  startMediaSendSession,
  logMediaSendCacheStep,
  logMediaSendNetworkStart,
  logMediaSendComplete,
  logMediaSendFailure,
} from "./media-telemetry";

const QUEUE_STORAGE_KEY = "@memories_upload_queue_v1";
const QUEUE_DIR_NAME = "upload_queue";
const getQueueDir = (): Directory | null => {
  if (Platform.OS === "web") return null;
  return new Directory(Paths.document, QUEUE_DIR_NAME);
};

/**
 * Interface representing an item stored in the persistent upload queue.
 */
export interface IQueuedMediaItem {
  /** Unique identifier for the queue item */
  id: string;
  /** Original file name */
  name: string;
  /** MIME content type */
  contentType: string;
  /** Path to persistent file copy on disk */
  localFilePath: string;
  /** Original source URI */
  originalUri: string;
  /** File size in bytes */
  fileSize?: number;
  /** Current upload status */
  status: "pending" | "uploading" | "completed" | "failed";
  /** Error message if upload failed */
  error?: string;
  /** Number of retry attempts */
  retryCount: number;
  /** Timestamp when enqueued */
  createdAt: number;
  /** Timestamp when successfully completed */
  completedAt?: number;
  /** Media metadata payload (EXIF, camera, dimensions, etc.) */
  metadata?: any;
  /** GPS / geographical coordinates */
  location?: { latitude: number; longitude: number; text?: string };
  /** Whether to suppress automatic memory generation */
  skipAutoMemory?: boolean;
}

/**
 * Interface for queue event notifications.
 */
export interface IUploadQueueEvent {
  type: "enqueued" | "started" | "progress" | "item_completed" | "item_duplicate" | "item_failed" | "completed" | "cancelled";
  item?: IQueuedMediaItem;
  media?: any;
  existingMediaId?: string;
  existingMediaUrl?: string;
  currentIndex?: number;
  totalItems?: number;
  uploadedCount?: number;
  duplicateCount?: number;
  failedCount?: number;
  lastError?: string;
  isBackground?: boolean;
}

type QueueListener = (event: IUploadQueueEvent) => void;

class UploadQueueManager {
  private inMemoryQueue: IQueuedMediaItem[] = [];
  private isProcessing: boolean = false;
  private isCancelled: boolean = false;
  private isBackgroundMode: boolean = false;
  private listeners: Set<QueueListener> = new Set();
  private isInitialized: boolean = false;

  /**
   * Subscribes a listener to queue lifecycle events.
   *
   * @param {QueueListener} listener - Callback function.
   * @returns {() => void} Unsubscribe function.
   */
  public subscribe(listener: QueueListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Emits an event to all subscribed listeners.
   *
   * @param {IUploadQueueEvent} event - Queue event payload.
   */
  private emit(event: IUploadQueueEvent): void {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.error("[UploadQueue] Error in queue listener:", err);
      }
    });
  }

  /**
   * Sets whether the current upload is running in background mode.
   *
   * @param {boolean} isBackground - True if background mode is enabled.
   */
  public setBackgroundMode(isBackground: boolean): void {
    this.isBackgroundMode = isBackground;
    logAndFlush("info", `[UploadQueue] Background mode set to: ${isBackground}`);
  }

  /**
   * Returns whether the queue is currently in background mode.
   *
   * @returns {boolean}
   */
  public getIsBackgroundMode(): boolean {
    return this.isBackgroundMode;
  }

  /**
   * Returns whether an upload loop is actively processing.
   *
   * @returns {boolean}
   */
  public getIsProcessing(): boolean {
    return this.isProcessing;
  }

  /**
   * Ensures the persistent queue directory exists on disk.
   */
  private async ensureQueueDir(): Promise<void> {
    const queueDir = getQueueDir();
    if (!queueDir) return;
    try {
      if (!queueDir.exists) {
        queueDir.create();
        console.log("[UploadQueue] Created upload_queue directory:", queueDir.uri);
      }
    } catch (err: any) {
      console.warn("[UploadQueue] Failed to ensure upload_queue dir:", err?.message || err);
    }
  }

  /**
   * Loads the persistent queue manifest from AsyncStorage.
   */
  public async init(): Promise<IQueuedMediaItem[]> {
    if (this.isInitialized) return this.inMemoryQueue;
    await this.ensureQueueDir();

    try {
      const raw = await AsyncStorage.getItem(QUEUE_STORAGE_KEY);
      if (raw) {
        const parsed: IQueuedMediaItem[] = JSON.parse(raw);
        // Reset any items that were left in "uploading" state before an app kill to "pending"
        this.inMemoryQueue = parsed.map((item) =>
          item.status === "uploading" ? { ...item, status: "pending" } : item
        );
      } else {
        this.inMemoryQueue = [];
      }
    } catch (err: any) {
      console.warn("[UploadQueue] Failed to load queue from storage:", err?.message || err);
      this.inMemoryQueue = [];
    }

    this.isInitialized = true;
    return this.inMemoryQueue;
  }

  /**
   * Saves the current in-memory queue to AsyncStorage.
   */
  private async persistQueue(): Promise<void> {
    try {
      await AsyncStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(this.inMemoryQueue));
    } catch (err: any) {
      console.error("[UploadQueue] Failed to persist queue to storage:", err?.message || err);
    }
  }

  /**
   * Gets all current items in the queue.
   *
   * @returns {IQueuedMediaItem[]} Current items.
   */
  public getQueue(): IQueuedMediaItem[] {
    return [...this.inMemoryQueue];
  }

  /**
   * Gets pending or uploading items count.
   *
   * @returns {number} Active count.
   */
  public getActiveItemsCount(): number {
    return this.inMemoryQueue.filter((i) => i.status === "pending" || i.status === "uploading").length;
  }

  /**
   * Gets failed items count.
   *
   * @returns {number} Failed count.
   */
  public getFailedItemsCount(): number {
    return this.inMemoryQueue.filter((i) => i.status === "failed").length;
  }

  /**
   * Copies incoming files into local persistent storage and enqueues them.
   * Protects against Android temporary content:// URI permission revocation when the app backgrounds.
   *
   * @param {Array<any>} rawFiles - Array of file descriptors from share intent.
   * @returns {Promise<IQueuedMediaItem[]>} Newly enqueued items.
   */
  public async enqueueFiles(rawFiles: any[]): Promise<IQueuedMediaItem[]> {
    await this.init();
    await this.ensureQueueDir();

    const newItems: IQueuedMediaItem[] = [];

    for (let i = 0; i < rawFiles.length; i++) {
      const f = rawFiles[i];
      const sourceUri = f.path || f.filePath || f.contentUri || f.uri || "";
      if (!sourceUri) continue;

      const id = `queue_${Date.now()}_${i}_${Math.random().toString(36).substring(2, 7)}`;
      const name = f.name || f.fileName || `shared_${Date.now()}_${i + 1}`;
      const contentType = f.mimeType || f.type || f.contentType || "image/jpeg";
      let localPath = sourceUri;

      // Copy file to persistent document directory if on native device
      const queueDir = getQueueDir();
      if (Platform.OS !== "web" && queueDir) {
        const sanitizedExt = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
        const targetFile = new File(queueDir, `${id}${sanitizedExt}`);
        const sourceFile = new File(sourceUri);

        try {
          // Attempt direct copy
          sourceFile.copy(targetFile);
          localPath = targetFile.uri;
          console.log(`[UploadQueue] Persisted ${sourceUri} -> ${targetFile.uri}`);
        } catch (copyErr: any) {
          console.warn(`[UploadQueue] copy failed for ${sourceUri}, checking fallback:`, copyErr?.message || copyErr);
          const rawSize = f.size || f.fileSize || 0;
          if (rawSize > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
            console.warn(`[UploadQueue] Skipping base64 fallback for large file (${rawSize} bytes), retaining original source URI.`);
            localPath = sourceUri;
          } else {
            try {
              // Fallback: read as base64 and write to persistent storage
              const base64 = await readFileAsBase64(sourceUri);
              await targetFile.write(base64);
              localPath = targetFile.uri;
              console.log(`[UploadQueue] Persisted via write fallback -> ${targetFile.uri}`);
            } catch (writeErr: any) {
              console.error(`[UploadQueue] Failed to persist file ${sourceUri}:`, writeErr?.message || writeErr);
              // Fall back to original URI if copy fails
              localPath = sourceUri;
            }
          }
        }
      }

      const queuedItem: IQueuedMediaItem = {
        id,
        name,
        contentType,
        localFilePath: localPath,
        originalUri: sourceUri,
        fileSize: f.size || f.fileSize || 0,
        status: "pending",
        retryCount: 0,
        createdAt: Date.now(),
        metadata: f.metadata,
        location: f.location,
        skipAutoMemory: f.skipAutoMemory ?? true,
      };

      this.inMemoryQueue.push(queuedItem);
      newItems.push(queuedItem);
    }

    await this.persistQueue();
    this.emit({
      type: "enqueued",
      totalItems: this.inMemoryQueue.length,
      isBackground: this.isBackgroundMode,
    });
    logAndFlush("info", `[UploadQueue] Enqueued ${newItems.length} items. Total queue length: ${this.inMemoryQueue.length}`);

    return newItems;
  }

  /**
   * Processes all pending and retryable items in the upload queue.
   *
   * @param {FirebaseServices} services - Active Firebase services reference.
   * @param {object} [options] - Options including whether running in background.
   * @returns {Promise<{ uploadedCount: number; duplicateCount: number; failedCount: number; lastError?: string }>}
   */
  public async processQueue(
    services: FirebaseServices,
    options?: { isBackground?: boolean }
  ): Promise<{ uploadedCount: number; duplicateCount: number; failedCount: number; lastError?: string }> {
    if (this.isProcessing) {
      console.log("[UploadQueue] Queue is already processing. Skipping redundant trigger.");
      return { uploadedCount: 0, duplicateCount: 0, failedCount: 0 };
    }

    await this.init();

    if (options?.isBackground !== undefined) {
      this.isBackgroundMode = options.isBackground;
    }

    this.isProcessing = true;
    this.isCancelled = false;

    if (Platform.OS !== "web") {
      activateKeepAwakeAsync("UploadQueueManager").catch(() => {});
    }

    let uploadedCount = 0;
    let duplicateCount = 0;
    let failedCount = 0;
    let lastError = "";

    this.emit({
      type: "started",
      totalItems: this.getActiveItemsCount(),
      isBackground: this.isBackgroundMode,
    });

    try {
      while (!this.isCancelled) {
        // Find next pending item
        const itemIndex = this.inMemoryQueue.findIndex((i) => i.status === "pending");
        if (itemIndex === -1) {
          // No more pending items
          break;
        }

        const item = this.inMemoryQueue[itemIndex];
        item.status = "uploading";
        await this.persistQueue();

        const pendingTotal = this.inMemoryQueue.filter((i) => i.status === "uploading" || i.status === "pending").length;
        const current1Idx = uploadedCount + duplicateCount + failedCount + 1;

        this.emit({
          type: "progress",
          item,
          currentIndex: current1Idx,
          totalItems: pendingTotal + current1Idx - 1,
          isBackground: this.isBackgroundMode,
        });

        const session = startMediaSendSession({
          source: "share_intent",
          totalItems: pendingTotal + current1Idx - 1,
          itemIndex: current1Idx,
          fileName: item.name,
          mimeType: item.contentType,
        });

        let base64 = "";
        try {
          if (item.fileSize && item.fileSize > MAX_SAFE_BASE64_FILE_SIZE_BYTES) {
            const sizeMb = (item.fileSize / (1024 * 1024)).toFixed(1);
            throw new Error(`File is too large (${sizeMb} MB). Maximum supported mobile upload size is 30 MB.`);
          }
          base64 = await readFileAsBase64(item.localFilePath);
          logMediaSendCacheStep(session, {
            cachePath: item.localFilePath,
            bytes: Math.round((base64.length * 3) / 4),
            success: true,
          });
        } catch (readErr: any) {
          const isTooLarge = String(readErr?.message || "").includes("File is too large");

          // Attempt fallback to originalUri if local cached file was removed or unreachable (unless file is too large)
          if (!isTooLarge && item.originalUri && item.originalUri !== item.localFilePath) {
            try {
              base64 = await readFileAsBase64(item.originalUri);
              item.localFilePath = item.originalUri;
              await this.persistQueue();
              logMediaSendCacheStep(session, {
                cachePath: item.originalUri,
                bytes: Math.round((base64.length * 3) / 4),
                success: true,
              });
            } catch {
              // Fallback also failed
            }
          }

          if (!base64) {
            const errText = readErr.message || "Could not read file from persistent storage.";
            if (isTooLarge) {
              console.warn(`[UploadQueue] Item ${item.name} exceeds upload size limit:`, errText);
            } else {
              console.error(`[UploadQueue] Failed to read item ${item.name}:`, errText);
            }
            item.status = "failed";
            item.error = errText;
            item.retryCount += 1;
            failedCount++;
            lastError = errText;
            await this.persistQueue();
            logMediaSendFailure(session, { step: "read", errorMessage: errText });
            this.emit({ type: "item_failed", item, lastError: errText, isBackground: this.isBackgroundMode });
            continue;
          }
        }

        if (this.isCancelled) {
          item.status = "pending";
          await this.persistQueue();
          break;
        }

        logMediaSendNetworkStart(session, Math.round((base64.length * 3) / 4));

        try {
          const res = await createMedia(services, {
            base64,
            title: item.name || "Untitled Media",
            name: item.name,
            contentType: item.contentType,
            href: item.metadata?.href || item.originalUri || item.name,
            skipAutoMemory: item.skipAutoMemory ?? true, // skipAutoMemory: true, default
            metadata: {
              ...(item.metadata || {}),
              href: item.metadata?.href || item.originalUri || item.name,
            },
            location: item.location,
          });

          const isDuplicate = res.isDuplicate || (typeof res.error === "string" && res.error.includes("already been uploaded"));

          if (res.status) {
            uploadedCount++;
            item.status = "completed";
            item.completedAt = Date.now();
            await this.deleteLocalFile(item.localFilePath);
            await this.persistQueue();

            logMediaSendComplete(session, {
              mediaId: (res.media as any)?.id,
              isDuplicate: res.isDuplicate,
              serverDiagnostics: res.serverDiagnostics,
              cacheCleaned: true,
            });

            this.emit({
              type: "item_completed",
              item,
              media: res.media,
              isBackground: this.isBackgroundMode,
            });
          } else if (isDuplicate) {
            duplicateCount++;
            item.status = "completed";
            item.completedAt = Date.now();
            await this.deleteLocalFile(item.localFilePath);
            await this.persistQueue();

            this.emit({
              type: "item_duplicate",
              item,
              existingMediaId: res.existingMediaId,
              existingMediaUrl: res.existingMediaUrl,
              isBackground: this.isBackgroundMode,
            });
          } else {
            failedCount++;
            lastError = res.error || "Server upload failure.";
            item.status = "failed";
            item.error = lastError;
            item.retryCount += 1;
            await this.persistQueue();

            logMediaSendFailure(session, {
              step: "server",
              errorMessage: lastError,
              errorCode: "SERVER_ERROR",
            });

            this.emit({ type: "item_failed", item, lastError, isBackground: this.isBackgroundMode });
          }
        } catch (uploadErr: any) {
          const isInterrupted =
            uploadErr?.isInterrupted ||
            uploadErr?.code === "functions/internal" ||
            (uploadErr?.message && (
              uploadErr.message.toLowerCase().includes("interrupted") ||
              uploadErr.message.toLowerCase().includes("sleep") ||
              uploadErr.message.toLowerCase().includes("internal")
            ));

          if (isInterrupted && item.retryCount < 3) {
            item.status = "pending";
            item.error = "Upload interrupted during device suspend. Queued for auto-resume upon wake.";
            item.retryCount += 1;
            await this.persistQueue();

            logAndFlush("warn", `[UploadQueue] Item ${item.id} interrupted during suspend. Kept as pending for auto-resume.`);
            logMediaSendFailure(session, {
              step: "upload",
              errorMessage: item.error,
              errorCode: "SUSPEND_INTERRUPTED",
            });
            break; // Pause processing until app awakens or reconnects
          } else {
            failedCount++;
            lastError = uploadErr.message || "Network upload error.";
            item.status = "failed";
            item.error = lastError;
            item.retryCount += 1;
            await this.persistQueue();

            logMediaSendFailure(session, {
              step: "upload",
              errorMessage: lastError,
              errorCode: "NETWORK_ERROR",
            });

            this.emit({ type: "item_failed", item, lastError, isBackground: this.isBackgroundMode });
          }
        }
      }

      // Purge successfully completed items from AsyncStorage to keep manifest lightweight
      await this.cleanupCompletedItems();
    } finally {
      if (Platform.OS !== "web") {
        deactivateKeepAwake("UploadQueueManager");
      }
      this.isProcessing = false;
    }

    if (this.isCancelled) {
      this.emit({ type: "cancelled", isBackground: this.isBackgroundMode });
    } else {
      this.emit({
        type: "completed",
        uploadedCount,
        duplicateCount,
        failedCount,
        lastError: failedCount > 0 ? lastError : undefined,
        isBackground: this.isBackgroundMode,
      });
    }

    logAndFlush(
      "info",
      `[UploadQueue] Batch processing finished: uploaded=${uploadedCount}, duplicates=${duplicateCount}, failed=${failedCount}, cancelled=${this.isCancelled}`
    );

    return { uploadedCount, duplicateCount, failedCount, lastError };
  }

  /**
   * Resets all failed items to "pending" status and triggers queue processing.
   *
   * @param {FirebaseServices} services - Active Firebase services.
   * @returns {Promise<{ uploadedCount: number; duplicateCount: number; failedCount: number }>}
   */
  public async retryFailedItems(
    services: FirebaseServices
  ): Promise<{ uploadedCount: number; duplicateCount: number; failedCount: number }> {
    await this.init();
    let resetCount = 0;

    this.inMemoryQueue.forEach((item) => {
      if (item.status === "failed") {
        item.status = "pending";
        item.error = undefined;
        resetCount++;
      }
    });

    if (resetCount > 0) {
      await this.persistQueue();
      logAndFlush("info", `[UploadQueue] Retrying ${resetCount} failed items.`);
      return this.processQueue(services);
    }
    return { uploadedCount: 0, duplicateCount: 0, failedCount: 0 };
  }

  /**
   * Dismisses and deletes all failed items from queue and persistent storage.
   */
  public async dismissFailedItems(): Promise<void> {
    await this.init();
    const remaining: IQueuedMediaItem[] = [];

    for (const item of this.inMemoryQueue) {
      if (item.status === "failed") {
        await this.deleteLocalFile(item.localFilePath);
      } else {
        remaining.push(item);
      }
    }

    this.inMemoryQueue = remaining;
    await this.persistQueue();
    logAndFlush("info", "[UploadQueue] Dismissed and deleted all failed items.");
  }

  /**
   * Cancels the active queue, stops in-flight tasks, and clears all items.
   */
  public async cancelQueue(): Promise<void> {
    this.isCancelled = true;
    this.isProcessing = false;

    for (const item of this.inMemoryQueue) {
      await this.deleteLocalFile(item.localFilePath);
    }

    this.inMemoryQueue = [];
    await this.persistQueue();
    this.emit({ type: "cancelled", isBackground: this.isBackgroundMode });
    logAndFlush("info", "[UploadQueue] Queue was cancelled and cleaned up.");
  }

  /**
   * Removes completed items from the manifest and in-memory list.
   */
  private async cleanupCompletedItems(): Promise<void> {
    this.inMemoryQueue = this.inMemoryQueue.filter((item) => item.status !== "completed");
    await this.persistQueue();
  }

  /**
   * Deletes a cached local file from disk.
   */
  private async deleteLocalFile(path: string): Promise<void> {
    const queueDir = getQueueDir();
    if (!path || Platform.OS === "web" || !queueDir) return;
    if (!path.startsWith(queueDir.uri)) return; // Only delete files stored within upload_queue directory

    try {
      const file = new File(path);
      if (file.exists) {
        file.delete();
      }
    } catch (err: any) {
      console.warn(`[UploadQueue] Failed to delete cache file ${path}:`, err?.message || err);
    }
  }
}

export const uploadQueue = new UploadQueueManager();

export interface IUploadQueueStatus {
  pendingCount: number;
  uploadingCount: number;
  failedCount: number;
  activeTotal: number;
  isProcessing: boolean;
  isBackground: boolean;
  items: IQueuedMediaItem[];
  retryFailed: (services: FirebaseServices) => Promise<any>;
  dismissFailed: () => Promise<void>;
  cancelQueue: () => Promise<void>;
}

/**
 * Reactive React hook tracking upload queue status, pending counts, and processing state.
 */
export function useUploadQueueStatus(): IUploadQueueStatus {
  const [items, setItems] = useState<IQueuedMediaItem[]>(() => uploadQueue.getQueue());
  const [isProcessing, setIsProcessing] = useState<boolean>(() => uploadQueue.getIsProcessing());
  const [isBackground, setIsBackground] = useState<boolean>(() => uploadQueue.getIsBackgroundMode());

  useEffect(() => {
    uploadQueue.init().then((q) => {
      setItems([...q]);
      setIsProcessing(uploadQueue.getIsProcessing());
      setIsBackground(uploadQueue.getIsBackgroundMode());
    });

    const unsubscribe = uploadQueue.subscribe(() => {
      setItems(uploadQueue.getQueue());
      setIsProcessing(uploadQueue.getIsProcessing());
      setIsBackground(uploadQueue.getIsBackgroundMode());
    });

    return unsubscribe;
  }, []);

  const pendingCount = items.filter((i) => i.status === "pending").length;
  const uploadingCount = items.filter((i) => i.status === "uploading").length;
  const failedCount = items.filter((i) => i.status === "failed").length;
  const activeTotal = pendingCount + uploadingCount;

  return {
    pendingCount,
    uploadingCount,
    failedCount,
    activeTotal,
    isProcessing,
    isBackground,
    items,
    retryFailed: (services: FirebaseServices) => uploadQueue.retryFailedItems(services),
    dismissFailed: () => uploadQueue.dismissFailedItems(),
    cancelQueue: () => uploadQueue.cancelQueue(),
  };
}

